import { reactive } from 'vue';
import { trackEvent } from '@/lib/analytics';
import { logWebHrError } from '@/core/logger/log-wrapper';
import { EVENT_NAMES, EVENT_SCREENS } from '@/definitions';
import { DOCUMENT_NAMES } from '@/core/field-names';

/**
 * Base analytics composable
 * @param {Object} options - Initial options
 * @param {string} options.screen - The screen name for tracking
 * @param {string} options.installationId - The installation id for tracking
 * @returns {Object} Analytics methods and state
 */
export function useAnalytics(options = {}) {
  const { screen = '', installationId = '' } = options;

  const params = reactive({
    currentScreen: screen,
    installationId,
  });

  function setScreen(newScreen) {
    params.currentScreen = newScreen;
  }

  function setInstallationId(newInstallationId) {
    params.installationId = newInstallationId;
  }

  function track(eventName, properties = {}) {
    trackEvent(eventName, {
      screen: params.currentScreen,
      installationId: params.installationId,
      ...properties,
    });
  }

  return {
    params,
    setScreen,
    setInstallationId,
    track,
  };
}

/**
 * Factory for creating onboarding review analytics tracking functions
 * @param {Object} options - Configuration options
 * @param {string} options.installationId - The installation ID
 * @param {Function} options.getDocuments - Function that returns the documents array
 * @returns {Object} Pre-configured tracking functions for onboarding review
 */
export function useOnboardingReviewAnalytics(options = {}) {
  const { installationId = '', getDocuments = () => [] } = options;

  const { track } = useAnalytics({
    screen: EVENT_SCREENS.ONBOARDING_REVIEW,
    installationId,
  });

  /**
   * Extract tax and super details from documents for tracking
   * @returns {Object} Details object for tracking
   */
  function getSubmitEventDetails() {
    try {
      const documents = getDocuments();
      const taxAndSuperFields = documents.find(
        (document) => document.name === DOCUMENT_NAMES.SUPER_API
      )?.fields;

      if (!taxAndSuperFields) {
        return {};
      }

      const residencyField =
        taxAndSuperFields['tfnDeclaration.taxDetails.residentStatus'];
      const tfnDeclaredField =
        taxAndSuperFields['tfnDeclaration.taxDetails.declareTrueAndCorrect'];
      const tfndSubmitted = tfnDeclaredField?.value === 'Checked';
      const superTypeField = taxAndSuperFields['superannuation.type'];

      return {
        details: {
          taxResidencyStatus: residencyField?.value,
          superFundType: superTypeField?.value,
          tfndSubmitted,
        },
      };
    } catch (e) {
      logWebHrError({
        message: 'Failed to extract submit event details',
        source: 'use-analytics',
        error: e,
      });
      return {};
    }
  }

  // Factory-created tracking functions
  function trackSubmitRequested() {
    track(EVENT_NAMES.ONBOARDING_SUBMIT_REQUESTED, getSubmitEventDetails());
  }

  function trackSubmitSuccess() {
    track(
      EVENT_NAMES.ONBOARDING_SUBMIT_SUCCESS_ACKNOWLEDGED,
      getSubmitEventDetails()
    );
  }

  function trackSubmitFailure() {
    track(
      EVENT_NAMES.ONBOARDING_SUBMIT_FAILURE_ACKNOWLEDGED,
      getSubmitEventDetails()
    );
  }

  return {
    track,
    trackSubmitRequested,
    trackSubmitSuccess,
    trackSubmitFailure,
  };
}
